import { createContext, type ReactNode, type FC, useContext, useEffect } from 'react';
import { useDonationNavigation } from './libs/useDonationNavigation';
import { useDonationStore } from './libs/useDonationStore';
import { useDonationTracker } from './libs/useDonationTracker';

export type DonationSummaryLabelProps = {
    summaryTitle: string;
    giftTypeLabel: string;
    giftTypeValue: string;
    amountLabel: string;
    feeLabel: string;
    totalLabel: string;
}

export interface DonationBlockProps {
    baseURL: string;
    donationConfig: {
        currency: string;
        currencySymbol: string;
        dataUrl: string;
        locale: string;
        pageGuid: string;
    };
    reCaptchaValidationConfig: {
        threshold: number;
    };
    personalDetailForm: {
        formID: string;
        locale: string;
    };
    debitForm: {
        formID: string;
        locale: string;
    };
    privacyLink: {
        enable: boolean;
        linkText: string;
        linkUrl: string;
        openNewTab: boolean;
    };
    paymentPageIntro: string;
    thankYouPageUrl: string;
    cancelPageUrl: string;
    giftTileSettings: {
        enable: boolean;
        imageUrl: string;
        baseAmount: number;
        message: {
            general: {
                once: string;
                monthly: string;
            };
            singlePerson: {
                once: string;
                monthly: string;
            };
            highNumber: {
                once: string;
                monthly: string;
            };
            lowNumber: {
                once: string;
                monthly: string;
            };
        };
    };
    sslText: string;
    labels: {
        stepOneNumber: string;
        stepOneLabel: string;
        stepTwoNumber: string;
        stepTwoLabel: string;
        stepThreeNumber: string;
        stepThreeLabel: string;
        onceSummaryTitle: string;
        monthlySummaryTitle: string;
        onceSummaryGiftLabel: string;
        monthlySummaryGiftLabel: string;
        onceSummaryGiftName: string;
        monthlySummaryGiftName: string;
        onceSummaryAmountLabel: string;
        monthlySummaryAmountLabel: string;
        onceSummaryFeeLabel: string;
        monthlySummaryFeeLabel: string;
        onceSummaryTotalLabel: string;
        monthlySummaryTotalLabel: string;
        onceMinimumAmountMessage: string;
        monthlyMinimumAmountMessage: string;
        detailPageIntro: string;
        detailPageDescription: string;
        paymentStepTitle: string;
        paymentStepSubtitle: string;
    };
    summaryLabels: {
        once: DonationSummaryLabelProps;
        monthly: DonationSummaryLabelProps;
    };
    backButton: {
        enable: boolean;
        buttonLabel: string;
    };
    tracker: {
        gaMeasurementId: string;
        landingPageDescription: string; // campaign or page title
        landingPageCode: string; // campaign code
    };
}

interface DonationBlockProviderProps extends DonationBlockProps {
    children: ReactNode;
}

export const DonationBlockContext = createContext<Partial<DonationBlockProps>>({});

export const useDonationBlockProps = (): Partial<DonationBlockProps> => {
    return useContext(DonationBlockContext);
};

const DonationBlockProvider: FC<DonationBlockProviderProps>= ({ children, ...restProps }) => {
    const providerValue = { ...restProps };

    const { initializeDonationStep } = useDonationNavigation();
    const { initializeDonationData } = useDonationStore();
    const { initializeDonationTracker } = useDonationTracker();

    const measurementId = providerValue.tracker?.gaMeasurementId ?? '';

    const summaryLabels: DonationBlockProps['summaryLabels'] = {
        once: {
            summaryTitle: providerValue.labels.onceSummaryTitle,
            giftTypeLabel: providerValue.labels.onceSummaryGiftLabel,
            giftTypeValue: providerValue.labels.onceSummaryGiftName,
            amountLabel: providerValue.labels.onceSummaryAmountLabel,
            feeLabel: providerValue.labels.onceSummaryFeeLabel,
            totalLabel: providerValue.labels.onceSummaryTotalLabel,
        },
        monthly: {
            summaryTitle: providerValue.labels.monthlySummaryTitle,
            giftTypeLabel: providerValue.labels.monthlySummaryGiftLabel,
            giftTypeValue: providerValue.labels.monthlySummaryGiftName,
            amountLabel: providerValue.labels.monthlySummaryAmountLabel,
            feeLabel: providerValue.labels.monthlySummaryFeeLabel,
            totalLabel: providerValue.labels.monthlySummaryTotalLabel,
        },
    };

    useEffect(() => {
        initializeDonationStep();
        initializeDonationData();
        initializeDonationTracker(measurementId);
    }, [initializeDonationStep, initializeDonationData, initializeDonationTracker, providerValue.tracker?.gaMeasurementId, measurementId]);

    return (
        <DonationBlockContext.Provider value={{ ...providerValue, summaryLabels }}>
            {children}
        </DonationBlockContext.Provider>
    );
};

export default DonationBlockProvider;
